<template>
  <div class="fullscreen-sdk-wrapper">
    <div class="modal-header">
      <button @click="handleClose" class="close-btn">
        <van-icon name="arrow-left" size="20" />
      </button>
      <span class="title">Card Payment</span>
      <div style="width: 20px"></div>
    </div>

    <div class="modal-content">
      <!-- Loading 遮罩 -->
      <div v-if="loading" class="loading-overlay">
        <van-loading size="24px" color="#FF1979">Loading...</van-loading>
      </div>

      <!-- SDK 容器 -->
      <div ref="sdkContainer" class="sdk-container payermax-card-frame"></div>

      <!-- 提交按钮 -->
      <button v-show="!loading" @click="submitPayment" :disabled="!formValid" class="submit-btn">
        {{ $t('pay_now') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'

interface SessionData {
  client_key: string
  session_key: string
  sandbox: boolean
}

interface Props {
  // 会话信息（pay-popup 已用 rsp_id 创建好）
  sessionData: SessionData
  // 商品（SDK 渲染成功后用它下单，需 rsp_id）
  product?: any
  // 下单附加参数（book_id/chapter_id/pay_entry）
  extra?: any
  // 支付类型：order=一次性，subscription=订阅
  paymentType: string
  // 订阅管理地址（订阅场景使用）
  mitManagementUrl?: string
  // 支付成功重定向地址
  returnUrl?: string
}

const props = withDefaults(defineProps<Props>(), {
  product: () => ({}),
  extra: () => ({}),
  paymentType: 'order',
  mitManagementUrl: '',
  returnUrl: ''
})

const emit = defineEmits<{
  close: []
  success: []
  failed: [error: string]
  'render-failed': []
}>()

// 前置组件下单走 createDropInOrder（/order/create，与收银台同接口）
const { createDropInOrder, createDropInPayment } = usePay()

const loading = ref(true)
const formValid = ref(false)
const sdkContainer = ref()
let cardInstance: any = null
let orderNo = ''  // 渲染成功后立即下单（/order/create）获得，submitPayment 直接用

// 初始化 SDK
const initSDK = async () => {
  loading.value = true
  try {
    const PM = (window as any).PMdropin
    if (!PM) {
      throw new Error('PMdropin SDK not loaded')
    }

    await nextTick()

    cardInstance = PM.create('card', {
      clientKey: props.sessionData.client_key,
      sessionKey: props.sessionData.session_key,
      sandbox: props.sessionData.sandbox,
    })

    // 官方用法：mount 接受 CSS 选择器字符串，挂载到第一个匹配的 DOM
    cardInstance.mount('.payermax-card-frame')

    // 组件加载完成事件（官方 'load'）：
    // - code=SUCCESS 渲染成功 → 立即创建订单（/order/create，对齐 master：进全屏建会话后即下单）
    // - 否则渲染失败 → 通知降级
    let loadFired = false
    cardInstance.on('load', async (res: any = {}) => {
      if (loadFired) return  // 防 load 事件重入：避免重复下单
      loadFired = true
      loading.value = false
      if (res?.code !== 'SUCCESS') {
        console.error('[PayermaxCardFullScreen] SDK 渲染失败，降级:', res)
        emit('render-failed')
        return
      }
      // 渲染成功 → 立即创建订单（/order/create），拿正式业务订单号
      const orderRes: any = await createDropInOrder(props.product, props.extra, 'card-payermax')
      if (!orderRes || orderRes.code !== 0) {
        emit('failed', 'Failed to create order')
        return
      }
      orderNo = orderRes.data.order_no
    })
    // 兜底：超时仍未触发 load（SDK 卡死）→ 关全屏降级，避免卡死
    setTimeout(() => {
      loading.value = false
      if (!loadFired) {
        console.error('[PayermaxCardFullScreen] SDK load 超时未触发，降级')
        emit('render-failed')
      }
    }, 5000)

    // 监听表单校验状态
    cardInstance.on('form-check', (d: any) => {
      formValid.value = d.isFormValid
    })
  } catch (error: any) {
    console.error('[PayermaxCardFullScreen] 初始化失败:', error)
    emit('failed', error.message || '初始化支付页面失败')
    loading.value = false
  }
}

// 拼接成功回调地址：在 return_url 基础上追加现有轮询逻辑所需参数
// （前置组件不经过收银台跳转，需前端自行补全 order_no/status/commit 等）
const buildSuccessUrl = () => {
  const sep = props.returnUrl.includes('?') ? '&' : '?'
  const params = new URLSearchParams({
    order_no: orderNo,
    payment_type: props.paymentType,
    pay_channel: 'card-payermax',
    status: 'SUCCESS',
    commit: 'confirm'
  })
  return `${props.returnUrl}${sep}${params.toString()}`
}

// 提交支付
// 防重：内部串行两个请求，期间重复点会拿同一个 orderNo 重复提交；复用 loading 锁住（按钮 v-show="!loading"）
const submitPayment = async () => {
  if (!cardInstance || !formValid.value || loading.value) return
  if (!orderNo) return  // 订单未创建（渲染未成功/下单失败）则不提交

  loading.value = true
  try {
    // 订单已在渲染成功时创建，直接用 orderNo
    // SDK 校验支付能力并获取 payment_token
    // 订阅场景：传 subscriptionPlan（用商品的 subscription_plan_config.payermax 官方原始结构，整体透传）
    // 一次性场景：后端建 session 未传金额，前端补 totalAmount（纯数字字符串，须与最终下单金额一致）
    const totalAmount = String(props.product?.discount_price || props.product?.price || '')
    const canMakePaymentParams = props.paymentType === 'subscription' && props.product?.subscription_plan_config?.payermax
      ? {
          mitManagementUrl: props.mitManagementUrl,
          subscriptionPlan: props.product.subscription_plan_config.payermax
        }
      : { totalAmount }
    const res = await cardInstance.emit('canMakePayment', canMakePaymentParams)

    if (res.code === 'APPLY_SUCCESS') {
      // 创建前置组件支付请求
      const payRes: any = await createDropInPayment({
        order_no: orderNo,
        session_key: props.sessionData.session_key,
        payment_token: res.data.paymentToken,
        pay_channel: 'card-payermax',
        payment_type: props.paymentType,
        mit_management_url: props.mitManagementUrl,
        return_url: props.returnUrl,
      })

      if (payRes.code === 0) {
        // 有 approve_url（3DS 挑战页，订阅场景才有）→ SDK 弹框处理 3DS，完成后跳成功回调
        if (payRes.data.approve_url) {
          cardInstance.create3DSPopup({
            url: payRes.data.approve_url
          })
            .then((result: any) => {
              if (result.code === '3DS_PROCESSED') {
                emit('success')
                window.location.href = buildSuccessUrl()
              }
            })
            .catch((err: any) => {
              console.error('[PayermaxCardFullScreen] 3DS 认证失败:', err)
              if (err.code === 'USER_CANCEL') {
                handleClose()
              } else {
                emit('failed', err.msg || '3DS authentication failed')
              }
            })
          return
        }
        // 无 approve_url（一次性支付直接成功）→ 跳转成功回调，复用现有轮询逻辑
        emit('success')
        window.location.href = buildSuccessUrl()
      } else {
        emit('failed', payRes.msg || 'Payment failed')
      }
    } else if (res.code === 'CANCELED') {
      // 用户取消 → 跳 status=CLOSED，触发挽留 + 上报（同收银台取消）
      handleClose()
    } else {
      emit('failed', res.msg || 'Payment not available')
    }
  } catch (error: any) {
    console.error('[PayermaxCardFullScreen] 支付失败:', error)
    emit('failed', error.message || 'Payment failed')
  } finally {
    loading.value = false
  }
}

// 拼接取消回调地址（status=CLOSED），复用现有 RechargeFailed 挽留 + 上报链路
const buildCancelUrl = () => {
  const sep = props.returnUrl.includes('?') ? '&' : '?'
  const params = new URLSearchParams({
    order_no: orderNo,
    payment_type: props.paymentType,
    pay_channel: 'card-payermax',
    status: 'CLOSED'
  })
  return `${props.returnUrl}${sep}${params.toString()}`
}

// 关闭（用户取消）→ 跳转 status=CLOSED，触发挽留弹窗 + 上报订单状态
const handleClose = () => {
  emit('close')
  window.location.href = buildCancelUrl()
}

// 清理
onBeforeUnmount(() => {
  if (cardInstance) {
    const container = sdkContainer.value
    if (container) container.innerHTML = ''
    cardInstance = null
  }
})

onMounted(() => {
  initSDK()
})
</script>

<style scoped lang="scss">
.fullscreen-sdk-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #fff;
  z-index: 99999;
  display: flex;
  flex-direction: column;

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 56px;
    padding: 0 16px;
    border-bottom: 1px solid #f0f0f0;

    .close-btn {
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      cursor: pointer;
    }

    .title {
      font-size: 18px;
      font-weight: 600;
      color: #333;
    }
  }

  .modal-content {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    position: relative;

    .loading-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      background: rgba(255, 255, 255, 0.9);
      z-index: 10;
    }

    .sdk-container {
      width: 100%;
      min-height: 300px;
    }

    .submit-btn {
      width: 100%;
      padding: 14px 0;
      margin-top: 20px;
      border: none;
      border-radius: 12px;
      background: linear-gradient(135deg, #FF394D 0%, #FF398F 100%);
      color: #fff;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;

      &:active {
        opacity: 0.8;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}
</style>
